export const en = {
  nav: {
    home: "Home",
    about: "About",
    services: "Services",
    resources: "Resources",
    blog: "Blog",
    faq: "FAQ",
    contact: "Contact",
    book: "Book a Consultation",
    menu: "Menu",
    close: "Close",
  },
  common: {
    chapter: "Chapter",
    learnMore: "Explore this service",
    readArticle: "Read article",
    viewAll: "View all",
    minRead: "min read",
    draft: "Editorial draft",
    published: "Published",
    updated: "Updated",
    reviewed: "Reviewed",
    by: "By",
    related: "Related reading",
    toc: "In this article",
    backToBlog: "Back to Aviah Insights",
    disclaimerTitle: "Important",
    skip: "Skip to content",
  },
  footer: {
    statement: ["PROTECT WHAT MATTERS.", "GROW WHAT'S POSSIBLE.", "BUILD WHAT LASTS."],
    company: "Company",
    services: "Services",
    resources: "Resources",
    legal: "Legal",
    contact: "Contact",
    about: "About",
    founder: "Founder",
    blog: "Blog",
    faq: "FAQ",
    privacy: "Privacy Policy",
    terms: "Terms of Use",
    disclosures: "Disclosures",
    accessibility: "Accessibility",
    editorial: "Editorial Policy",
    bookCta: "Book a Consultation",
    appointment: "By appointment",
    rights: "All rights reserved.",
    poweredBy: "Powered by",
    miniDisclaimer:
      "Website content is general educational information and does not constitute tax, legal, accounting or investment advice. Submitting a form does not establish a professional relationship.",
  },
  home: {
    seo: {
      title: "Aviah Financial — Financial & Business Consulting | Lake Nona, Orlando",
      description:
        "Integrated financial and business consulting in Lake Nona, Orlando. Business consulting, financial consulting, asset protection, estate planning and strategic investments. English & Español.",
    },
    hero: {
      eyebrow: "Financial & Business Consulting — Lake Nona, Orlando",
      lines: ["CLARITY FOR TODAY.", "STRATEGY FOR TOMORROW.", "A LEGACY FOR WHAT COMES NEXT."],
      body: "Aviah Financial brings financial, business and strategic guidance together under one trusted relationship — helping individuals, families, entrepreneurs and businesses understand their options, protect what they have built and move forward with confidence.",
      primary: "Book a Consultation",
      secondary: "Explore Our Services",
      micro: ["Lake Nona • Orlando, Florida", "In-person & virtual consultations", "Appointment required"],
      scroll: "Scroll",
    },
    marquee: ["Clarity", "Strategy", "Legacy", "Precision", "Discretion", "Lake Nona — Orlando"],
    trust: {
      eyebrow: "Experience. Credentials. Accountability.",
      title: "A practice built on verifiable work.",
    },
    numbers: {
      chapter: "More Than Numbers",
      title: "Every financial decision carries a human story.",
      p1: "Behind every tax decision, growing business, family asset, financial goal or future investment is something deeply personal: years of work, sacrifice, family, ambition, security and legacy.",
      p2: "Aviah Financial was created around the belief that financial guidance should not leave people feeling confused or intimidated. It should create clarity. It should educate. It should help people make informed decisions with confidence.",
      quote: "The future belongs to those who plan for it.",
    },
    strategy: {
      chapter: "The Integrated View",
      title: "One perspective. Multiple dimensions of your financial life.",
      copy: "Important financial decisions are interconnected. Select any dimension to see how it relates to the rest of your picture.",
      hub: ["You", "Your Family", "Your Business", "Your Legacy"],
      hint: "Tap a dimension to see its connections.",
      relations: {
        tax: ["Accounting", "Business", "Legacy"],
        accounting: ["Tax", "Business"],
        assets: ["Estate", "Legacy", "Business"],
        estate: ["Assets", "Legacy", "Family"],
        business: ["Tax", "Assets", "Estate", "Legacy"],
        investments: ["Assets", "Legacy"],
        legacy: ["Assets", "Estate", "Family"],
      },
    },
    services: {
      chapter: "Explore Our Services",
      title: "Five areas. One integrated financial perspective.",
      copy: "Each area is handled as part of a complete picture — never as an isolated transaction.",
    },
    founder: {
      chapter: "Founder & CEO",
      title: "Built through experience. Defined by leadership.",
      titleLines: ["Built through experience.", "Defined by leadership."],
      p1: "Long before Aviah Financial became a company, its foundation was being built through years of experience in financial services, administration, operations, leadership and business.",
      p2: "Each stage strengthened a different capability — from understanding financial decisions and client needs to developing organizational discipline, managing complex environments and building a broader business perspective.",
      p3: "Today, those experiences converge into one integrated approach: clarity before complexity, strategy before uncertainty, and purpose behind every decision.",
      bioP1: "Escarling Martínez began her professional journey at 18 in the insurance industry with MetLife, and went on to build experience across corporate operations at TJX Companies in Boston, administration within the Emergency Department at Mass General Hospital, and a degree in Accounting and Business Administration.",
      bioP2: "That combined foundation — financial services, corporate operations, healthcare administration, leadership and accounting — became the ground on which Aviah Financial was built.",
      cta: "Meet the founder",
      stages: [
        { lines: ["Financial", "Foundation"], micro: "Early career", d: "Early professional experience built a foundation in financial services, client relationships and informed decision-making." },
        { lines: ["Operational", "Discipline"], d: "Experience in corporate operations strengthened organization, administration, process management and business discipline." },
        { lines: ["Leadership"], d: "Growing professional responsibility developed stronger leadership, communication, decision-making and the ability to manage complex environments." },
        { lines: ["Business", "Perspective"], d: "Exposure to business administration and international operations broadened the ability to understand organizations beyond a single financial decision." },
        { lines: ["Precision", "Under Pressure"], d: "Working within demanding professional environments strengthened discipline, attention to detail, communication and the ability to make careful decisions under pressure." },
        { lines: ["Accounting", "+ Business"], d: "Formal education in Accounting and Business Administration connected practical experience with a stronger financial and organizational foundation." },
        { lines: ["Integrated", "Strategy"], d: "Years of financial, operational, administrative and leadership experience converged into a more complete view of how individuals, families and businesses should approach important decisions." },
        { lines: ["Aviah", "Financial"], d: "A financial and business consulting firm built around one belief: important decisions should never be made in isolation." },
      ],
      finale: {
        lines: ["Every experience", "built toward", "a clearer path forward."],
        tagline: ["Protect what matters.", "Grow what is possible.", "Build what lasts."],
      },
    },
    mission: {
      chapter: "Mission & Vision",
      missionLabel: "Mission",
      missionWords: "Educate. Elevate. Empower.",
      missionCopy:
        "To bring clarity to complexity and strategy to every decision — helping clients understand their options, protect what they have built, pursue what is possible and move forward with greater confidence.",
      visionLabel: "Vision",
      visionCopy:
        "A new era of financial and business consulting where strategy is intelligent, protection is intentional and legacy is built to endure.",
      line: "The future belongs to those who plan for it.",
    },
    serve: {
      chapter: "Who We Serve",
      title: "Strategy should fit the person. Not the other way around.",
      items: [
        { t: "Individuals", d: "Personal finances organized with clarity and care." },
        { t: "Families", d: "Protection and planning across generations." },
        { t: "Entrepreneurs", d: "Structure and guidance from the first decision." },
        { t: "Small Businesses", d: "Organization, administration and growth support." },
        { t: "Companies", d: "Coordinated financial and administrative strategy." },
        { t: "Investors", d: "Evaluation aligned with long-term objectives." },
      ],
    },
    method: {
      chapter: "The Aviah Method",
      title: "Your situation first. The strategy second.",
      steps: [
        { n: "01", t: "Listen", d: "We begin by understanding your current situation, needs, priorities and goals." },
        { n: "02", t: "Analyze", d: "We review the information and identify the areas that require attention." },
        { n: "03", t: "Build the Strategy", d: "We determine which services or strategies may provide value and explain the process, documentation and next steps." },
        { n: "04", t: "Move Forward Together", d: "We maintain clear communication throughout the entire process." },
      ],
    },
    privacy: {
      title: "Your information deserves the same protection as your future.",
      copy: "Privacy, discretion and trust are fundamental principles in Aviah Financial's relationship with every client. Information is handled professionally and discreetly, with an emphasis on confidentiality and transparent communication.",
      pillars: [
        { t: "Privacy", d: "Discretion is not a courtesy. It is the standard." },
        { t: "Transparency", d: "You understand the process at every step." },
        { t: "Precision", d: "Important decisions deserve careful attention." },
      ],
    },
    why: {
      chapter: "Why Aviah",
      title: "Why clients choose an integrated approach.",
      items: [
        { n: "01", t: "The Complete Picture", d: "Important decisions are interconnected — and treated that way." },
        { n: "02", t: "Personalized Strategy", d: "Recommendations begin with your circumstances, not a template." },
        { n: "03", t: "Clear Communication", d: "Complex topics are explained in language you can understand." },
        { n: "04", t: "Privacy & Discretion", d: "Confidentiality and trust are fundamental to every relationship." },
        { n: "05", t: "Experience, Credentials & Accountability", d: "Professional experience, disciplined processes and accountability guide every engagement." },
      ],
    },
    location: {
      chapter: "Lake Nona, Orlando",
      title: "Professional guidance, right here in Lake Nona.",
      features: [
        "Lake Nona, Southeast Orlando",
        "Minutes from Orlando International Airport (MCO)",
        "Free parking",
        "In-person consultations",
        "Virtual consultations",
        "Appointment required",
      ],
      directions: "Get Directions",
      book: "Book Your Consultation",
      hoursLabel: "Office hours",
    },
    insights: {
      chapter: "Aviah Insights",
      title: "Financial education, written to be understood.",
      copy: "Editorial guidance on taxes, business, protection and legacy — in English and Spanish.",
      cta: "Visit Aviah Insights",
    },
    final: {
      title: "Clarity can start with one conversation.",
      copy: "Whether you are organizing your finances, growing a business, protecting what you have built or planning what comes next — the first step is understanding your options.",
      cta: "Book Your Consultation",
      or: "Or call",
    },
  },
  about: {
    seo: {
      title: "About Aviah Financial — Financial Guidance with a Human Point of View",
      description:
        "The story of Aviah Financial and its founder Escarling Martínez — a career across insurance, corporate operations, hospital administration and accounting, built into one integrated consulting practice in Lake Nona, Orlando.",
    },
    eyebrow: "About Aviah Financial",
    title: "Financial guidance with a human point of view.",
    intro:
      "Aviah Financial is a financial and business consulting practice in Lake Nona, Orlando — created to bring together the guidance people usually have to chase across multiple professionals.",
    origin: {
      chapter: "Why Aviah Exists",
      title: "Important decisions should never be made in isolation.",
      p1: "Over the years, our founder observed the same pattern: individuals, families, entrepreneurs and companies needed help across multiple areas of their financial and business lives — but had to go to a different professional for each need, and the pieces rarely spoke to each other.",
      p2: "Aviah Financial was created to offer a more integrated approach: a space where individuals, families, entrepreneurs, small businesses, companies and investors can access guidance that starts with their complete situation — not with an isolated problem.",
    },
    founderTitle: "The founder",
    values: {
      chapter: "Our Values",
      title: "Seven principles that guide every engagement.",
      items: [
        { t: "Integrity Above Interest", d: "Every recommendation is formulated with the client's best interest as the priority." },
        { t: "Precision Over Speed", d: "Important decisions are approached carefully and accurately — never rushed." },
        { t: "Discretion as Standard", d: "Privacy is not an extra courtesy. It is an essential expectation." },
        { t: "Education", d: "You should understand the process and the decisions being considered." },
        { t: "Transparency", d: "Processes, services and recommendations are communicated clearly." },
        { t: "Personalized Strategy", d: "Every person, family and business has different goals and circumstances." },
        { t: "Results", d: "We focus on meaningful progress, practical outcomes and solutions aligned with your goals." },
      ],
    },
    office: {
      chapter: "The Office",
      title: "Lake Nona, Orlando.",
      copy: "A professional office in one of Orlando's most forward-looking districts — minutes from Orlando International Airport, with in-person and virtual consultations by appointment.",
    },
  },
  servicesPage: {
    seo: {
      title: "Services — Business, Financial, Asset Protection, Estate & Investments | Aviah Financial",
      description:
        "Five integrated service areas: business consulting, financial consulting, asset protection, estate planning and strategic investments. Lake Nona, Orlando. English & Español.",
    },
    eyebrow: "Services",
    title: "Integrated guidance across your financial life.",
    copy: "Five areas, coordinated through one relationship — so each decision is made with the complete picture in view.",
  },
  resourcesPage: {
    seo: {
      title: "Resources — Financial Education Center | Aviah Financial",
      description:
        "Checklists, guides and financial education from Aviah Financial. Education first — no email required. Lake Nona, Orlando.",
    },
    eyebrow: "Resources",
    title: "A financial education center, open to everyone.",
    copy: "Practical checklists and guides you can use today. Education first — nothing here is hidden behind a form.",
    checklistTitle: "Working checklists",
    checklists: [
      {
        t: "Tax preparation checklist",
        items: [
          "Prior-year tax return, if available",
          "W-2s, 1099s and other income statements",
          "Identification for each filer and dependent",
          "Records of deductible expenses",
          "Business income and expense records, if applicable",
          "ITIN or Social Security documentation, where applicable",
        ],
      },
      {
        t: "Business organization checklist",
        items: [
          "Chosen business structure documented",
          "EIN and state registrations in order",
          "Separate business bank account",
          "Basic bookkeeping routine in place",
          "Payroll approach defined, if you have employees",
          "Annual filing calendar noted",
        ],
      },
      {
        t: "Before your first consultation",
        items: [
          "A short list of your goals and concerns",
          "Any notices or letters you want reviewed",
          "Recent financial statements, if you have them",
          "Questions — written down, in any order",
        ],
      },
    ],
    note: "Do not send sensitive documents (SSN, full ITIN numbers, bank statements, tax documents) through the website. Bring them to your consultation instead.",
    ctaTitle: "Want a guide tailored to your situation?",
    ctaCopy: "The most useful checklist is the one built around your actual circumstances.",
  },
  blogPage: {
    seo: {
      title: "Aviah Insights — Financial Education in English & Spanish",
      description:
        "Aviah Insights: editorial financial education on taxes, business, asset protection, estate planning and investments — written to be understood. Lake Nona, Orlando.",
    },
    eyebrow: "Aviah Insights",
    title: "Ideas that help you decide.",
    copy: "Editorial financial education — no clickbait, no promises, just clarity.",
    all: "All",
  },
  faqPage: {
    seo: {
      title: "FAQ — Aviah Financial | Lake Nona, Orlando",
      description:
        "Answers to common questions about Aviah Financial: services, consultations, location, bilingual support and how the first appointment works.",
    },
    eyebrow: "Frequently Asked Questions",
    title: "Everything worth asking before we meet.",
    items: [
      { q: "What does Aviah Financial do?", a: "Aviah Financial is a financial and business consulting practice. We bring together five areas — business consulting, financial consulting, asset protection, estate planning and strategic investments — under one integrated relationship, so your decisions are made with the complete picture in view." },
      { q: "Who does Aviah Financial serve?", a: "Individuals, families, entrepreneurs, small businesses, companies and investors — primarily in Orlando and Lake Nona, with virtual consultations available where legally appropriate." },
      { q: "Do you work with both individuals and businesses?", a: "Yes. Many of our clients are both: business owners whose personal and business finances need to be considered together. That intersection is exactly where an integrated approach adds the most value." },
      { q: "Do you offer virtual consultations?", a: "Yes. Consultations are available in person at our Lake Nona office and virtually, where legally appropriate." },
      { q: "Can I visit the office?", a: "Yes — by appointment. Our office is at 6900 Tavistock Lakes Blvd, Suite 400, Orlando, FL 32827, in Lake Nona, with free parking." },
      { q: "Where is Aviah Financial located?", a: "In Lake Nona, Southeast Orlando — 6900 Tavistock Lakes Blvd, Suite 400, Orlando, FL 32827, minutes from Orlando International Airport (MCO)." },
      { q: "Is an appointment required?", a: "Yes. Every client relationship begins with a scheduled consultation, so we can dedicate uninterrupted time to your situation." },
      { q: "How does the first consultation work?", a: "We listen first. We review your current situation, needs, priorities and goals, then explain which areas may deserve attention, what the process looks like and what documentation would be needed. You leave with clarity — not pressure." },
      { q: "How do I know which service I need?", a: "You don't have to. Most situations touch more than one area, which is why the first conversation looks at the complete picture before recommending anything." },
      { q: "Do you offer bilingual assistance?", a: "Yes. Full service is available in English and Spanish — consultations, documentation review and ongoing communication." },
      { q: "What information should I bring to my first consultation?", a: "A short list of your goals and concerns, any notices or letters you would like reviewed, and recent financial statements if available. Never send sensitive documents — SSN, full ITIN numbers, bank or tax documents — through the website; bring them to your consultation." },
      { q: "Can multiple services be coordinated together?", a: "Yes — that coordination is the point. Tax decisions affect business structure; business decisions affect estate plans. We evaluate the relationships between areas before recommending any single step." },
    ],
  },
  contactPage: {
    seo: {
      title: "Contact — Book a Consultation | Aviah Financial, Lake Nona",
      description:
        "Request a consultation with Aviah Financial in Lake Nona, Orlando. In-person and virtual appointments. Call (407) 340-9141. English & Español.",
    },
    eyebrow: "Contact",
    title: "Let's talk about what comes next.",
    copy: "Tell us a little about your situation and we will confirm your consultation. Every request is reviewed personally.",
    form: {
      name: "Full name",
      phone: "Phone",
      email: "Email",
      preferredContact: "Preferred contact method",
      contactOptions: { phone: "Phone", email: "Email", text: "Text message" },
      service: "Service you're interested in",
      services: {
        "business-consulting": "Business Consulting",
        "tax-accounting": "Financial Consulting",
        "asset-protection": "Asset Protection",
        "estate-planning": "Estate Planning",
        "strategic-investments": "Strategic Investments",
        other: "Other / Not sure yet",
      },
      type: "Consultation type",
      typeOptions: { "in-person": "In person — Lake Nona office", virtual: "Virtual" },
      date: "Preferred date",
      time: "Preferred time",
      message: "How can we help?",
      messagePlaceholder: "A few sentences are enough. Please do not include Social Security numbers, full ITIN numbers, account numbers or tax documents.",
      consent:
        "I understand that submitting this form does not establish a professional, legal, tax, accounting or investment advisory relationship.",
      submit: "Request Consultation",
      sending: "Sending…",
      successTitle: "Request received.",
      successCopy:
        "Thank you for reaching out. Your consultation request has been received and will be reviewed personally. We will contact you through your preferred method to confirm your appointment.",
      errorCopy: "Something went wrong. Please try again or call us at (407) 340-9141.",
      sensitiveError: "For your protection, please do not include Social Security or full ID numbers in this form.",
      required: "This field is required.",
    },
    aside: {
      title: "The office",
      hoursTitle: "Office hours",
      note: "Consultations are by appointment, in person or virtual.",
      neverTitle: "What this form never asks for",
      neverCopy: "Social Security numbers, full ITIN numbers, bank account numbers, passport scans or tax documents. If your matter requires secure document exchange, that happens through a separate, protected process after your consultation.",
    },
  },
  legal: {
    updated: "Last updated: July 2026",
    privacy: {
      title: "Privacy Policy",
      sections: [
        { h: "Our commitment", p: ["Privacy and discretion are fundamental principles at Aviah Financial. This policy explains what information this website collects, why, and how it is handled."] },
        { h: "What we collect", p: ["If you submit the consultation form, we receive the details you provide: name, phone, email, preferred contact method, service of interest, consultation preference and your message.", "We do not ask for — and ask that you never send — Social Security numbers, full ITIN numbers, bank account numbers, passport scans, tax documents or financial statements through this website."] },
        { h: "How information is used", p: ["Information you submit is used solely to respond to your request, schedule and prepare for your consultation, and communicate with you about it.", "We do not sell your information. We do not share it with third parties except as required to operate the website (for example, our email delivery provider) or as required by law."] },
        { h: "Storage and security", p: ["Consultation requests are stored in a secured database. The website is served over HTTPS and protected forms use server-side validation, rate limiting and bot protection.", "No method of transmission over the internet is 100% secure; we encourage you to share sensitive documents only through the protected channels established after your consultation."] },
        { h: "Your choices", p: ["You may request that we correct or delete the information you submitted by contacting us at (407) 340-9141."] },
        { h: "Changes", p: ["This policy may be updated as the website evolves. The current version is always available on this page. Final legal copy is reviewed by qualified counsel before launch."] },
      ],
    },
    terms: {
      title: "Terms of Use",
      sections: [
        { h: "Acceptance", p: ["By using this website you accept these terms. If you do not agree, please do not use the site."] },
        { h: "Educational content only", p: ["All content on this website is general educational information. It is not tax, legal, accounting or investment advice, and it does not consider your individual circumstances.", "Submitting a form, sending a message or reading content on this site does not establish a professional, legal, tax, accounting or investment advisory relationship with Aviah Financial."] },
        { h: "No guarantees", p: ["We make no guarantees regarding outcomes, refunds, returns or results of any kind. Individual circumstances vary."] },
        { h: "Acceptable use", p: ["You agree not to misuse this website, attempt to disrupt its operation, or submit false, misleading or harmful information through its forms."] },
        { h: "Intellectual property", p: ["The Aviah name, logo and site content are the property of Aviah Financial and may not be reproduced without written permission."] },
        { h: "Review", p: ["These terms are reviewed by qualified counsel before launch and may be updated; the current version is always published here."] },
      ],
    },
    accessibility: {
      title: "Accessibility Statement",
      sections: [
        { h: "Our commitment", p: ["Aviah Financial is committed to a website that is usable by everyone, including people with disabilities. We aim for WCAG 2.2 Level AA conformance."] },
        { h: "Measures we take", p: ["Semantic HTML and logical heading structure.", "Keyboard navigability and visible focus states.", "Form labels and error descriptions.", "Contrast-compliant color combinations.", "Support for reduced-motion preferences."] },
        { h: "Feedback", p: ["If you encounter an accessibility barrier, call us at (407) 340-9141 and we will provide the information in an alternative format and address the issue."] },
      ],
    },
    editorial: {
      title: "Editorial Policy",
      sections: [
        { h: "Purpose", p: ["Aviah Insights exists to educate. Articles explain concepts, processes and questions — they do not sell outcomes."] },
        { h: "Standards", p: ["No clickbait, no guaranteed results, no individualized advice presented as universal advice.", "Articles are written or reviewed by the professionals named as authors, published bilingually, and dated with publication, update and review dates."] },
        { h: "Corrections", p: ["If an error is identified, we correct it and update the article's review date. To report a concern, call (407) 340-9141."] },
      ],
    },
    disclosures: {
      title: "Disclosures",
      sections: [
        { h: "General", p: ["Content on this website is general educational information and does not constitute tax, legal, accounting, investment or immigration legal advice. Individual circumstances vary; consult a qualified professional about your situation."] },
        { h: "Tax", p: ["Tax information presented is general in nature. No outcome, refund amount or tax result is guaranteed."] },
        { h: "Investments", p: ["Nothing on this website is an offer, solicitation or recommendation of any security or investment product. All investing involves risk, including possible loss of principal. No return is guaranteed."] },
        { h: "Legal and estate matters", p: ["Aviah Financial is not a law firm. Where legal documents or legal services require an attorney, we work alongside qualified, independent legal professionals."] },
      ],
    },
  },
  authorPage: {
    seo: {
      title: "Escarling Martínez — Founder & CEO | Aviah Financial",
      description:
        "Escarling Martínez, Founder & CEO of Aviah Financial — a professional journey across insurance, corporate operations, hospital administration and accounting.",
    },
    eyebrow: "Author & Founder",
    role: "Founder & CEO, Aviah Financial",
    bioTitle: "Background",
    articles: "Articles by Escarling",
  },
  notFound: {
    title: "This page has moved — or never existed.",
    copy: "The address you followed doesn't match any page on this site.",
    cta: "Return home",
  },
};
